/**
 * Listener ranking from Sharetribe reviews of the provider.
 * Featured homepage placement must never affect these numbers.
 */

import { REVIEW_TYPE_OF_PROVIDER } from './types';
import { isListingOnline, sortAvailableNowFirst } from './presence';
import { listingCardPhotoUrl } from './listingCardPhoto';

/** Sam, Oct 21: no-photo listeners go below every listener with a photo (stable; order kept inside each group). */
export const photosFirst = (listings = []) => {
  const has = l => { try { return !!listingCardPhotoUrl(l); } catch (e) { return false; } };
  return [...listings.filter(has), ...listings.filter(l => !has(l))];
};

export const RANKING_SORT_KEY = 'ranking';
export const RECOMMENDED_SORT_KEY = 'recommended';
export const AVAILABLE_NOW_PARAM = 'availableNow';
export const AVAILABLE_NOW_OFF = '0';
export const AVAILABLE_NOW_ONLY = 'only';

export const emptyReviewStats = () => ({ average: 0, count: 0 });

export const statsFromReviews = (reviews = []) => {
  const ratings = reviews
    .filter(review => review?.attributes?.type === REVIEW_TYPE_OF_PROVIDER)
    .map(review => Number(review?.attributes?.rating))
    .filter(rating => rating >= 1 && rating <= 5);
  if (ratings.length === 0) {
    return emptyReviewStats();
  }
  const sum = ratings.reduce((total, rating) => total + rating, 0);
  return {
    count: ratings.length,
    average: Math.round((sum / ratings.length) * 10) / 10,
  };
};

export const reviewStatsMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    if (item?.listingId) {
      acc[item.listingId] = {
        average: Number(item.average) || 0,
        count: Number(item.count) || 0,
        ...(typeof item.quote === 'string' && item.quote ? { quote: item.quote } : {}),
      };
    }
    return acc;
  }, {});
};

/**
 * Search URL `availableNow` is parsed as a number when it is `0`.
 * Treat both the string and the number as "sort off".
 * `only` is a real filter (hide offline listeners) and still sorts online first.
 */
export const isAvailableNowOnlyFilter = search => {
  const value = search?.[AVAILABLE_NOW_PARAM];
  return (
    value === AVAILABLE_NOW_ONLY ||
    value === 'true' ||
    value === true ||
    value === '1' ||
    value === 1
  );
};

export const isAvailableNowFirstEnabled = search => isAvailableNowOnlyFilter(search);

export const isAvailableNowFirstChipOn = search =>
  isAvailableNowFirstEnabled(search) && !isAvailableNowOnlyFilter(search);

/** Toggle the sort chip: default (omit) ↔ `availableNow=0`. Clears the online-only filter. */
export const nextAvailableNowSortParam = search => {
  if (isAvailableNowOnlyFilter(search)) {
    return undefined;
  }
  return isAvailableNowFirstEnabled(search) ? AVAILABLE_NOW_OFF : undefined;
};

/** Toggle the online-only filter: `availableNow=only` ↔ default sort of everyone. */
export const nextAvailableNowOnlyParam = search =>
  isAvailableNowOnlyFilter(search) ? undefined : AVAILABLE_NOW_ONLY;

/** Exclusive Any time chip — always show online + offline. */
export const availableNowParamForAnyTime = () => AVAILABLE_NOW_OFF;

/** Exclusive Available now chip — hide listeners who are not online. */
export const availableNowParamForAvailableNow = () => AVAILABLE_NOW_ONLY;

/**
 * Marketplace query for the Available now chip. Any time omits the flag
 * so offline listeners still appear.
 *
 * @param {Object} [search]
 * @returns {{ pub_availableNow?: true }}
 */
export const availableNowApiParams = search =>
  isAvailableNowOnlyFilter(search) ? { pub_availableNow: true } : {};

export const filterListingsByAvailableNow = (listings = [], presenceMap = {}, search) => {
  if (!isAvailableNowOnlyFilter(search)) {
    return listings;
  }
  // Oct 27 2026 (Sam): Available Now = the Available flag (publicData.availableNow), same as
  // the green dots. No heartbeat required; a live presence still counts too.
  return listings.filter(
    listing => !!listing?.id?.uuid && isListingOnline(listing.id.uuid, presenceMap, listing)
  );
};

export const sortByHighestRated = (listings = [], statsMap = {}) => {
  return [...listings].sort((a, b) => {
    const sa = statsMap[(a?.id?.uuid)] || emptyReviewStats();
    const sb = statsMap[(b?.id?.uuid)] || emptyReviewStats();
    if (sb.average !== sa.average) {
      return sb.average - sa.average;
    }
    return sb.count - sa.count;
  });
};

const isRecommendedSort = sortKey => !sortKey || sortKey === RECOMMENDED_SORT_KEY;

/**
 * Apply search ranking.
 * Recommended (default) is available-now first, then rating.
 * Highest rated is rating only. Featured flags are ignored.
 */
export const sortSearchListings = (
  listings = [],
  { statsMap = {}, presenceMap = {}, sortKey, availableNowFirst } = {}
) => {
  let next = [...listings];
  if (isRecommendedSort(sortKey) || sortKey === RANKING_SORT_KEY) {
    next = sortByHighestRated(next, statsMap);
  }
  if (isRecommendedSort(sortKey) || availableNowFirst) {
    next = sortAvailableNowFirst(next, presenceMap);
  }
  if (isRecommendedSort(sortKey)) {
    next = photosFirst(next);
  }
  return next;
};

/** Sam, Oct 21: home cards use the recommended order: Available Now, reviews, completeness; no-photo last. */
export const rankHomeListenerCards = (listings = [], { isAvailable = () => false, statsMap = {} } = {}) => {
  const complete = l => {
    const pd = l?.attributes?.publicData || {};
    const about = String(l?.attributes?.description || '').trim().length;
    const topics = new Set([...(pd.topics || []), ...(pd.topicPaths || [])].filter(t => t && t !== 'other')).size;
    return (about >= 200 ? 2 : about >= 80 ? 1 : 0) + (topics >= 5 ? 2 : topics >= 3 ? 1 : 0);
  };
  const st = l => statsMap[l?.id?.uuid] || { average: 0, count: 0 };
  const idx = new Map(listings.map((l, i) => [l, i]));
  const sorted = [...listings].sort((a, b) =>
    (isAvailable(b) ? 1 : 0) - (isAvailable(a) ? 1 : 0) ||
    (st(b).count || 0) - (st(a).count || 0) ||
    (st(b).average || 0) - (st(a).average || 0) ||
    complete(b) - complete(a) ||
    idx.get(a) - idx.get(b)
  );
  return photosFirst(sorted);
};
